// @ts-nocheck

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { HistoryBreakLinesLimit } from '../../constants/history';
import theme from '../../styled-components/dark';
import { OutcomeStatuses, type OutcomeStatus } from '../../types/history';
import { isLabelsTooLong } from '../../utils/basketItems';
import { getOutcomeColorFromStatus } from '../../utils/outcome';
import HistoryItemOutcomeCardProgressBarContainer from '../HistoryItemOutcomeCardProgressBar/HistoryItemOutcomeCardProgressBarContainer';

const BetbuilderBetTitle = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  color: ${theme.History.outcome.defaultContent.color};
  box-sizing: border-box;
  position: relative;
  padding: 0.25em 0 0.25em 1.2em;

  ::before,
  ::after {
    left: 0.3em;
  }

  ${({ $hasCurrentLineProgressBar }) => css`
    ${$hasCurrentLineProgressBar
      ? css`
          padding: 0em 0 0.25em 1.2em;
        `
      : undefined}
  `}
`;

const Row = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  width: 100%;
  line-height: 1em;
  color: ${({ theme: innerTheme }) => innerTheme.common.colors.defaultFontColor};

  ::after,
  ::before {
    content: '';
    position: absolute;
    height: 50%;
    width: 1px;
    background: ${theme.History.outcome.mymatchContent.bulletsColor};
    left: 0.2em;
  }

  ::before {
    bottom: var(--HistoryItemOutcomeMyMatchLine-rowBeforeBottom, 50%);
    transform-origin: 0 100%;
    display: initial;
  }

  ::after {
    top: var(--HistoryItemOutcomeMyMatchLine-rowAfterTop, 50%);
    transform-origin: 0 0;
    display: initial;
  }

  :first-child::before,
  :last-child::after {
    display: none;
  }

  ::after {
    content: ' ';
    display: block;
    clear: both;
  }
`;

const BetbuilderOutcomeLabel = styled.span`
  ${({ $color }) => css`
    color: ${$color};
    color: var(--status-color, ${$color});
  `};

  text-align: left;
  font-weight: bold;
`;

const SelectionCircle = styled.div<SelectionCirclePropsType>`
  border-radius: 50%;
  background: ${theme.History.outcome.mymatchContent.bulletsColor};
  width: 0.6em;
  height: 0.6em;
  position: absolute;
  top: 50%;
  transform: translateX(-45%) translateY(-50%);
  z-index: 1;
  left: 0.2em;
  box-sizing: content-box;
  border: 0.15em solid ${theme.History.betslip.backgroundColor};
`;

const FlexWrapperAndAlignItems = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const OutcomeWrapper = styled.span`
  ${({ $shouldBreakLine }) =>
    $shouldBreakLine
      ? css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        `
      : css`
          display: inline-block;
        `}
`;

const BetTitle = styled.span`
  font-size: 0.87em;
  color: var(--MyMatchLineBetTitle-color, ${theme.History.outcome.mymatchContent.betTitleColor});

  ${({ $shouldBreakLine }) =>
    !$shouldBreakLine
      ? css`
          display: inline-block;
          padding-right: 0.4em;
        `
      : undefined}
`;

const StyledHistoryItemOutcomeCardProgressBarContainer = styled(HistoryItemOutcomeCardProgressBarContainer)`
  transform: translateY(-3px);
`;

type HistoryItemOutcomeMyMatchLineProps = {
  outcomeLabel: string;
  outcomeStatus: OutcomeStatus;
  betTitle: string;
  showCircle: boolean;
  noBreakLine?: boolean;
  outcomeId?: number;
};

const HistoryItemOutcomeMyMatchLine = ({
  outcomeLabel,
  outcomeStatus,
  betTitle,
  showCircle,
  noBreakLine,
  outcomeId,
}: HistoryItemOutcomeMyMatchLineProps) => {
  const rowNodeRef = useRef<HTMLDivElement | null>(null);
  const selectionCircleNodeRef = useRef<HTMLDivElement | null>(null);
  const [historyItemOutcomeCardProgressBarNode, setHistoryItemOutcomeCardProgressBarNode] =
    useState<HTMLDivElement>(null);
  const finalBetTitle = useMemo(() => `${betTitle}${outcomeLabel}`, [betTitle, outcomeLabel]);
  const shouldBreakLine = useMemo(
    () => isLabelsTooLong([finalBetTitle], HistoryBreakLinesLimit) && !noBreakLine,
    [finalBetTitle, noBreakLine],
  );

  const labelColor = useMemo(() => {
    if (outcomeStatus === OutcomeStatuses.RUNNING) {
      return '#ffffff';
    }

    if (outcomeStatus) {
      return getOutcomeColorFromStatus(outcomeStatus);
    }

    return theme.History.outcome.mymatchContent.outcomeRunningColor;
  }, [outcomeStatus]);

  useLayoutEffect(() => {
    const hasProgressBar = !!historyItemOutcomeCardProgressBarNode;

    if (hasProgressBar) {
      const { height: progressBarHeight } = historyItemOutcomeCardProgressBarNode.getBoundingClientRect();

      if (progressBarHeight) {
        const selectionCircleNode = selectionCircleNodeRef.current;

        if (selectionCircleNode) {
          selectionCircleNode.style.top = `calc(50% - ${Math.round(progressBarHeight / 2)}px)`;
        }

        const rowNode = rowNodeRef.current;

        if (rowNode) {
          rowNode.style.setProperty('--HistoryItemOutcomeMyMatchLine-rowBeforeBottom', '75%');
          rowNode.style.setProperty('--HistoryItemOutcomeMyMatchLine-rowAfterTop', '25%');
        }
      }
    }
  }, [historyItemOutcomeCardProgressBarNode]);

  return (
    <Row ref={rowNodeRef}>
      <BetbuilderBetTitle $hasCurrentLineProgressBar={!!historyItemOutcomeCardProgressBarNode}>
        {showCircle ? (
          <SelectionCircle
            ref={selectionCircleNodeRef}
            $hasCurrentLineProgressBar={!!historyItemOutcomeCardProgressBarNode}
          />
        ) : null}
        <FlexWrapperAndAlignItems>
          <OutcomeWrapper $shouldBreakLine={shouldBreakLine}>
            <BetTitle $shouldBreakLine={shouldBreakLine}>{betTitle}</BetTitle>
            <BetbuilderOutcomeLabel $color={labelColor}>{outcomeLabel}</BetbuilderOutcomeLabel>
          </OutcomeWrapper>

          <StyledHistoryItemOutcomeCardProgressBarContainer
            ref={setHistoryItemOutcomeCardProgressBarNode}
            outcomeId={outcomeId}
          />
        </FlexWrapperAndAlignItems>
      </BetbuilderBetTitle>
    </Row>
  );
};

export default React.memo<HistoryItemOutcomeMyMatchLineProps>(HistoryItemOutcomeMyMatchLine);
